import type { SxProps, Theme } from "@mui/material/styles";
import type { KeyboardEvent } from "react";
import { FORM_FIELD_BASE_SX, FORM_FIELD_EXTRA_SMALL_SX } from "../formFieldSize";
import type { FormFieldSize } from "../types";

// --------------------------------- TYPES ---------------------------------
type EntradaSx = Exclude<NonNullable<SxProps<Theme>>, readonly unknown[]>;

type ComporSxFormInputOptions = {
	readonly size: FormFieldSize;
	readonly sx?: SxProps<Theme>;
	readonly value: unknown;
	readonly highlightActiveSelection: boolean;
	readonly activeHighlightColor: string;
	readonly error: boolean;
};

// -------------------------------- HELPERS --------------------------------
const criarSxSelecaoAtiva = (color: string): SxProps<Theme> => ({
	"& .MuiFormLabel-root": {
		color: `${color} !important`,
	},
	"& .MuiInputLabel-root": {
		color: `${color} !important`,
	},
	"& .MuiInputLabel-root.Mui-focused": {
		color: `${color} !important`,
	},
	"& .MuiOutlinedInput-notchedOutline": {
		borderColor: `${color} !important`,
	},
	"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
		borderColor: `${color} !important`,
	},
	"& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
		borderColor: `${color} !important`,
	},
	"& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
		borderColor: `${color} !important`,
	},
});

function possuiValorAtivo(value: unknown): boolean {
	if (value === null || value === undefined) return false;
	if (Array.isArray(value)) return value.length > 0;
	if (typeof value === "string") return value.trim().length > 0;
	if (typeof value === "number") return Number.isFinite(value);
	if (typeof value === "boolean") return value;
	if (value instanceof Date) return !Number.isNaN(value.getTime());
	if (typeof value === "object") return Object.keys(value).length > 0;
	return true;
}

function adicionarSx(partes: Array<EntradaSx | boolean>, sx?: SxProps<Theme>): void {
	if (!sx) return;
	if (Array.isArray(sx)) {
		partes.push(...(sx as Array<EntradaSx | boolean>));
		return;
	}
	partes.push(sx as EntradaSx);
}

// ------------------------------- FUNCTIONS -------------------------------
export function submeterFormularioAoPressionarEnter(
	evento: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
): void {
	const possuiModificador = evento.shiftKey || evento.ctrlKey || evento.altKey || evento.metaKey;
	if (evento.defaultPrevented || evento.key !== "Enter" || possuiModificador || evento.nativeEvent.isComposing)
		return;

	evento.preventDefault();
	if (evento.repeat || evento.currentTarget.disabled || evento.currentTarget.readOnly) return;

	const formulario = evento.currentTarget.form;
	if (!formulario) return;
	const botaoSubmit = Array.from(formulario.elements).find(
		(elemento): elemento is HTMLButtonElement | HTMLInputElement =>
			(elemento instanceof HTMLButtonElement && elemento.type === "submit") ||
			(elemento instanceof HTMLInputElement && (elemento.type === "submit" || elemento.type === "image")),
	);
	if (botaoSubmit?.matches(":disabled")) return;
	formulario.requestSubmit(botaoSubmit);
}

export function comporSxFormInput({
	size,
	sx,
	value,
	highlightActiveSelection,
	activeHighlightColor,
	error,
}: Readonly<ComporSxFormInputOptions>): SxProps<Theme> {
	const partes: Array<EntradaSx | boolean> = [FORM_FIELD_BASE_SX];
	if (size === "extraSmall") partes.push(FORM_FIELD_EXTRA_SMALL_SX);
	adicionarSx(partes, sx);
	// O destaque entra por último; erros mantêm a prioridade visual.
	if (highlightActiveSelection && possuiValorAtivo(value) && !error)
		adicionarSx(partes, criarSxSelecaoAtiva(activeHighlightColor));
	return partes;
}
